import { useEffect } from "react";
import api from "../lib/api";

/**
 * useAttributionTracker — fires the install-attribution telemetry beacon
 * once per browser session per source.
 *
 * Read `?src=` from the current URL on first mount; if present and on
 * the allowed list (the backend whitelist mirrors this), persist the
 * source to localStorage (`install-attribution-source`) so the
 * `appinstalled` listener in index.js can attribute the eventual PWA
 * install, and POST a one-shot beacon to
 * `/api/telemetry/install-attribution`.
 *
 * Dedupe is per (browser-session × source), not per page-load, so a
 * pilot reloading the share link doesn't double-log. Same source on
 * subsequent navigations within a session is a no-op.
 *
 * Mount this once at the SPA root (App.js) — running it per route
 * would re-evaluate the params after the SPA's client-side navigation
 * and risk double-logging if the param sticks across routes.
 */
const KNOWN_SOURCES = new Set(["qr", "share", "tournament", "social", "direct"]);

export default function useAttributionTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("src");
      if (!raw) return;
      const src = raw.trim().toLowerCase();
      if (!KNOWN_SOURCES.has(src)) return;
      // Persist for `appinstalled` (index.js reads this key when firing
      // /install-completed). Always store the latest source — a pilot
      // who first arrived via QR but later clicked a share link is
      // expressing fresh intent from the share channel.
      window.localStorage.setItem("install-attribution-source", src);
      // Once per session per source.
      const sessKey = `install-attr-pinged-${src}`;
      if (window.sessionStorage.getItem(sessKey) === "1") return;
      window.sessionStorage.setItem(sessKey, "1");
      api
        .post("/telemetry/install-attribution", { source: src })
        .catch(() => {});
    } catch {
      /* private mode / storage blocked — fail silent */
    }
  }, []);
}
